import {Opening} from "../../../../../api/Openings";
import {QuoteOpening} from "../../../../../api/QuoteOpenings";
import {
    DistanceCol,
    NominalWidth,
    NumberCol,
    Select2Col,
    StringCol,
    Table, useFilteredTableData
} from "../../../../../misc/scroller/Table";
import {useMemoryTableFetcher} from "../../freeformchanges/products/add/useMemoryTableFetcher";
import {Checkbox2Col} from "../../ShopTableCol";
import {
    locationTransitions,
    openingTypes,
} from "../../../quote/openings/QuoteOpenings";
import {labels} from "../../../quote/openings/Const";
import {useProjectUnitSystem} from "../../ProjectName";
import useWindowSize from "react-use/lib/useWindowSize";
import {EditContext} from "../../../../../misc/scroller/EditProvider";
import {useContext, useRef} from "react";
import {Checkbox} from "@mui/material";
import {useSyncedRef} from "../../../../../misc/SyncedRef";
import {PhaseCol} from "../PhaseCol";

const tableName = "submittal-opening-selector"

export function OpeningsTab(props: {
    value: OpeningWithSelected[];
    onChange(value: OpeningWithSelected[]): void;
}) {

    const data = useMemoryTableFetcher(props.value, props.onChange);
    const sys = useProjectUnitSystem();
    const win = useWindowSize();

    const valueRef = useSyncedRef(props.value)
    const lastClickedRef = useRef<number | null>(null);

    if(!sys) return null;

    return (
        <div style={{
            display: "flex", flexDirection: "column", flex: 1,
            minHeight: win.height * 0.6,
            marginTop: -20,
            marginBottom: -20,
            marginLeft: -24,
            marginRight: -24,
        }}>
            <Table<OpeningWithSelected>
                name={tableName}
                columns={[
                    {
                        renderName: () => <CheckBoxAll tableName={tableName} keyName="selected" />,
                        render: (v) => <CheckboxItem keyName="selected" value={v} lastClickedRef={lastClickedRef} />,
                        width: 50,
                    },
                    StringCol("Opening", "name"),
                    NumberCol("Qty", "qty"),
                    StringCol("Floor", "floor"),
                    PhaseCol<Opening>(),
                    Checkbox2Col("Ext", "exterior"),
                    StringCol("Location", "locationOne"),
                    Select2Col<Opening>("To/From", "locationTransition", locationTransitions),
                    StringCol<Opening>("Location 2", "locationTwo"),
                    Select2Col<Opening>("Type", "openingType", openingTypes),
                    NominalWidth<Opening>("Nominal Width", sys, "nominalWidth", "inactiveDoorWidth"),
                    DistanceCol("Nominal Height", sys, "nominalHeight"),
                    StringCol("Hand", "handing"),
                    Select2Col<Opening>("Label", "label", labels)
                ]}
                fetch={data.fetch}
                fetchDeps={[props.value]}
                onChange={async v => {
                    let update = valueRef.current.map(h => {
                        if (h.id === v.id) {
                            return Object.assign({}, v, {
                                updatedAt: new Date().toISOString(),
                            });
                        }

                        return h;
                    });

                    valueRef.current = update;
                    data.onChange(update);
                }}
            />
        </div>
    )
}

export type OpeningWithSelected = (Opening|QuoteOpening)&{selected: boolean}

function CheckBoxAll(props: {
    tableName: string;
    keyName: string;
}) {
    const ctx = useContext(EditContext);

    // only the rows that pass the column filters
    const dt = useFilteredTableData<any>(props.tableName);

    // @ts-ignore
    const all = dt.every(s => !!s[props.keyName])

    return (
        <Checkbox size="small" checked={all} onClick={e => {
            if(all) {
                dt.forEach(s => {
                    ctx.update(Object.assign({}, s, {
                        [props.keyName]: false,
                    }));
                })
            } else {
                dt.forEach(s => {
                    ctx.update(Object.assign({}, s, {
                        [props.keyName]: true
                    }))
                })
            }
        }} />
    )
}

function CheckboxItem(props: {
    keyName: string;
    value: any;
    lastClickedRef: {current: number | null};
}) {

    const ctx = useContext(EditContext);
    const value = props.value[props.keyName];
    const visible = useFilteredTableData<any>(tableName);

    return (
        <Checkbox size="small" checked={value} onClick={e => {
            const next = !value;

            // shift-click applies the new value to every visible row between the last clicked row and this one
            const lastId = props.lastClickedRef.current;
            const fromIndex = lastId === null ? -1 : visible.findIndex(v => v.id === lastId);
            const toIndex = visible.findIndex(v => v.id === props.value.id);

            if(e.shiftKey && fromIndex !== -1 && toIndex !== -1) {
                const start = Math.min(fromIndex, toIndex);
                const end = Math.max(fromIndex, toIndex);

                visible.slice(start, end + 1).forEach(row => {
                    ctx.update(Object.assign({}, row, {
                        [props.keyName]: next,
                    }));
                });
            } else {
                ctx.update(Object.assign({}, props.value, {
                    [props.keyName]: next,
                }));
            }

            props.lastClickedRef.current = props.value.id;
        }} />
    )
}